/* eslint-disable unicorn/no-null */
/* eslint-disable @typescript-eslint/no-magic-numbers */
/* eslint-disable import/no-extraneous-dependencies */
import { clsx, type ClassValue } from 'clsx';
import { useLayoutEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';
import { UAParser } from 'ua-parser-js';

import type { Question as QuestionType } from '@/slices/testSlice';

import packageJson from '../package.json';

// eslint-disable-next-line import/prefer-default-export
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => matchMedia(query).matches);

  useLayoutEffect(() => {
    const mediaQuery = matchMedia(query);

    function onMediaQueryChange(): void {
      setMatches(mediaQuery.matches);
    }

    mediaQuery.addEventListener('change', onMediaQueryChange);

    return (): void => {
      mediaQuery.removeEventListener('change', onMediaQueryChange);
    };
  }, [query]);

  return matches;
}

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Basic option type
export type QuestionOption = {
  type: string;
  value: string;
  evidenceType?: string;
  evidenceId?: string | null;
  explanationType?: string | null;
  explanation?: string | null;
  isCorrect?: boolean | null;
};

// Hint type
type Hint = {
  type: string;
  value: string;
};

// Metadata structure
type QuestionMetaData = {
  questionFormatData: {
    options: QuestionOption[];
  };
  hints: Hint[];
  explanationType: string;
  explanation: string | null;
  voiceover?: {
    type: 'audio' | 'text' | null;
    value: string | null;
  };
};

// Language-specific question content
export type LanguageSpecificQuestionContent = {
  questionText: string;
  questionMediaType: string | null;
  questionMedia: string;
  metaData: QuestionMetaData;
};

// Unified question object (covers both original and mapped new format)
export type UnifiedQuestionObject = {
  id: string;
  type?: number;
  difficulty?: number;
  format: string;
  mediumMapping: {
    [language: string]: LanguageSpecificQuestionContent;
  };
};

// Type to represent either format as input
type InputQuestion = UnifiedQuestionObject | QuestionType;

export function handleMCQQuestion(question: InputQuestion): UnifiedQuestionObject {
  if ('mediumMapping' in question) {
    // It's already in the unified format, return as is
    return question;
  }
  // It's in the new format, map to unified format
  return {
    id: question.id,
    difficulty: undefined,
    format: 'MCQ',
    mediumMapping: {
      en: {
        questionText: question.text || '',
        questionMediaType: question.type,
        questionMedia: question.media || '',
        metaData: {
          questionFormatData: {
            options: question.options.map((option) => ({
              ...option,
              evidenceType: '',
              evidenceId: null,
              explanationType: null,
              explanation: null,
              isCorrect: null,
            })),
          },
          hints: [], // Set to an empty array as per the requirement
          explanationType: 'none',
          explanation: null,
        },
      },
    },
  };
}

export const formatTime = (timeInSeconds: number): string => {
  const result = new Date(Math.round(timeInSeconds) * 1000).toISOString().slice(11, 19);
  if (Number(result.slice(0, 2)) > 0) {
    return result;
  }
  return result.slice(3);
};

export const getVersion = (): string => {
  return (packageJson as { version: string }).version;
};

export const getDeviceInfo = () => {
  const parser = new UAParser();
  const currentOs = parser.getOS().name;
  const currentBrowser = parser.getBrowser().name;
  const currentModel = parser.getDevice().model;

  return { currentOs, currentBrowser, currentModel };
};

export const isElectron = () => {
  if (
    typeof process !== 'undefined' &&
    typeof process.versions === 'object' &&
    !!process.versions.electron
  ) {
    return true;
  }
  if (
    typeof navigator === 'object' &&
    typeof navigator.userAgent === 'string' &&
    navigator.userAgent.includes('Electron')
  ) {
    return true;
  }
  return false;
};

export const getDesktopVersion = async (): Promise<string> => {
  if (window.electronAPI?.getAppVersion) {
    return window.electronAPI.getAppVersion();
  }
  return '0.0.0';
};
